import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import {
  listOpportunities,
  generateOpportunities,
  setOpportunityStatus,
} from "@/lib/opportunities.functions";
import { FitCard } from "@/components/opportunities/FitCard";
import { runTwinMeeting, listTwinMeetings } from "@/lib/meetings.functions";
import {
  listRadarSignals,
  refreshRadar,
  actRadarSignal,
  draftRadarOutreach,
} from "@/lib/radar.functions";
import { RadarCard } from "@/components/opportunities/RadarCard";
import { NetworkReach } from "@/components/opportunities/NetworkReach";
import { createDealFromMeeting } from "@/lib/deals.functions";
import { Sparkles, Handshake, ChevronDown, ChevronUp, Copy, Bot, Radar } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useHydratedTranslation } from "@/components/marketing/Layout";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/opportunities")({
  head: () =>
    buildSeo({
      path: "/opportunities",
      title: "Opportunities — Reality Twin",
      description:
        "Twin-to-Twin matches surfaced from the Reality Twin network — partnership signals your AI Twin generates for your company.",
      noindex: true,
    }),
  component: Page,
});

/**
 * Analyst points with the transcript quote that supports them. Quotes were
 * verified server-side against the transcript; anything unsupported was already
 * dropped, so whatever renders here is real. Older meetings stored plain
 * strings and simply show no quote.
 */
function CitedList({ items, label, hue }: { items: unknown; label: string; hue: string }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs mb-1.5" style={{ color: hue }}>
        {label}
      </div>
      <ul className="space-y-2">
        {items.map((x: any, i: number) => {
          const point = typeof x === "string" ? x : (x?.point ?? "");
          const quote = typeof x === "string" ? null : (x?.quote ?? null);
          if (!point) return null;
          return (
            <li key={i} className="text-sm text-muted-foreground">
              <div className="flex gap-1.5">
                <span className="shrink-0" style={{ color: hue }}>
                  ·
                </span>
                <span>{point}</span>
              </div>
              {quote && (
                <div className="ml-3.5 mt-1 border-l-2 border-border pl-2 text-[11.5px] italic text-muted-foreground/80">
                  "{quote}"
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const MEETING_PHASES = [
  "app.pages.opportunities.phase0",
  "app.pages.opportunities.phase1",
  "app.pages.opportunities.phase2",
  "app.pages.opportunities.phase3",
  "app.pages.opportunities.phase4",
];

function Page() {
  const t = useHydratedTranslation();
  const navigate = useNavigate();
  const draftDealFn = useServerFn(createDealFromMeeting);
  const fetchList = useServerFn(listOpportunities);
  const generate = useServerFn(generateOpportunities);
  const setStatus = useServerFn(setOpportunityStatus);
  const startMeeting = useServerFn(runTwinMeeting);
  const fetchMeetings = useServerFn(listTwinMeetings);

  const fetchRadar = useServerFn(listRadarSignals);
  const scanRadar = useServerFn(refreshRadar);
  const actSignal = useServerFn(actRadarSignal);
  const draftOutreach = useServerFn(draftRadarOutreach);

  const [items, setItems] = useState<any[]>([]);
  const [reach, setReach] = useState<any>(null);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [meetingsUid, setMeetingsUid] = useState<string | undefined>();
  const [signals, setSignals] = useState<any[]>([]);
  const [scanning, setScanning] = useState(false);
  const [draftingId, setDraftingId] = useState<string | null>(null);
  const [gen, setGen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [meetingFor, setMeetingFor] = useState<string | null>(null);
  // The opportunity whose meeting brief is open. A meeting without a stated
  // objective is two twins improvising, so the owner is asked first.
  const [briefFor, setBriefFor] = useState<any>(null);
  const [phase, setPhase] = useState(0);
  const [openMeeting, setOpenMeeting] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const [r, m, rad] = await Promise.all([
      fetchList(),
      fetchMeetings().catch(() => ({ meetings: [] })),
      fetchRadar().catch(() => ({ signals: [] })),
    ]);
    setItems(r.opportunities);
    setReach((r as any).reach ?? null);
    setMeetings(m.meetings ?? []);
    setMeetingsUid((m as any).userId);
    setSignals(rad.signals ?? []);
    setLoading(false);
  }

  async function draftDeal(meetingId: string) {
    try {
      await draftDealFn({ data: { meetingId } });
      toast.success(t("app.pages.deals.dealDrafted"));
      navigate({ to: "/deals" });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    }
  }

  async function doScan() {
    setScanning(true);
    try {
      const r = await scanRadar({});
      toast.success(
        r.created > 0
          ? t("app.pages.radar.scanned", { count: r.created })
          : t("app.pages.radar.scannedNone"),
      );
      const rad = await fetchRadar().catch(() => ({ signals: [] }));
      setSignals(rad.signals ?? []);
    } catch (e: any) {
      toast.error(e?.message ?? "Scan failed");
    } finally {
      setScanning(false);
    }
  }

  /** Write the outreach for one signal, on request. */
  async function writeDraft(id: string) {
    setDraftingId(id);
    try {
      const r = await draftOutreach({ data: { id } });
      setSignals((s) => s.map((x) => (x.id === id ? { ...x, draft_message: r.draft } : x)));
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setDraftingId(null);
    }
  }

  async function useSignal(sig: any) {
    if (sig.draft_message) {
      try {
        await navigator.clipboard.writeText(sig.draft_message);
      } catch {
        /* clipboard may be blocked; the draft is still visible */
      }
    }
    toast.success(t("app.pages.radar.copied", { name: sig.counterpart_name ?? "" }));
    setSignals((s) => s.filter((x) => x.id !== sig.id));
    actSignal({ data: { id: sig.id, status: "sent" } }).catch(() => {});
  }

  async function dropSignal(id: string) {
    setSignals((s) => s.filter((x) => x.id !== id));
    actSignal({ data: { id, status: "dismissed" } }).catch(() => {});
  }
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!meetingFor) return;
    setPhase(0);
    const iv = setInterval(() => setPhase((p) => Math.min(p + 1, MEETING_PHASES.length - 1)), 6000);
    return () => clearInterval(iv);
  }, [meetingFor]);

  async function doGenerate() {
    setGen(true);
    try {
      const r = await generate({});
      // "Nothing found" is a real result, not a failure. Saying so plainly
      // beats a success toast claiming zero new matches.
      toast.success(
        r.created > 0
          ? t("app.pages.opportunities.generated", { n: r.created })
          : t("app.pages.opportunities.generatedNone", {
              defaultValue:
                "No new fits held up under checking. Nothing was added — that is the honest answer.",
            }),
      );
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setGen(false);
    }
  }

  /** Record a decision. Dismissing is permanent: the counterpart is excluded
   *  from future matching, so the same card never returns. */
  async function decide(id: string, status: string) {
    setItems((list) =>
      status === "dismissed" || status === "blocked"
        ? list.filter((x) => x.id !== id)
        : list.map((x) => (x.id === id ? { ...x, status } : x)),
    );
    try {
      await setStatus({ data: { id, status: status as any } });
      if (status === "dismissed") {
        toast.success(
          t("app.pages.opportunities.dismissedForGood", {
            defaultValue: "Dismissed — this company will not be suggested again.",
          }),
        );
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
      load();
    }
  }

  async function meet(o: any, objective: string | null) {
    setBriefFor(null);
    setMeetingFor(o.id);
    try {
      const r = await startMeeting({ data: { opportunityId: o.id, objective } });
      toast.success(t("app.pages.opportunities.meetingCompleted", { name: r.counterpart.name }));
      await load();
      setOpenMeeting(r.meetingId);
      document.getElementById("twin-meetings")?.scrollIntoView({ behavior: "smooth" });
    } catch (e: any) {
      toast.error(e.message ?? t("app.pages.opportunities.meetingFailed"));
    } finally {
      setMeetingFor(null);
    }
  }

  function copyIntro(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(t("app.pages.opportunities.introCopied")));
  }

  return (
    <DashboardShell
      title={t("app.pages.opportunities.title")}
      subtitle={t("app.pages.opportunities.subtitle")}
      actions={
        <button
          onClick={doGenerate}
          disabled={gen}
          className="rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
        >
          <Sparkles className={`h-3.5 w-3.5 ${gen ? "animate-pulse" : ""}`} />{" "}
          {gen
            ? t("app.pages.opportunities.matching")
            : t("app.pages.opportunities.generateMatches")}
        </button>
      }
    >
      {/* Deal Radar — proactive signals with drafted outreach */}
      <div className="glass-strong rounded-2xl p-5 mb-6 border border-[oklch(0.82_0.18_168_/_0.25)]">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Radar className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
            <div className="font-display text-lg font-semibold">{t("app.pages.radar.title")}</div>
            {signals.length > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-[oklch(0.82_0.18_168_/_0.15)] text-[oklch(0.82_0.18_168)] font-mono">
                {signals.length}
              </span>
            )}
          </div>
          <button
            onClick={doScan}
            disabled={scanning}
            className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Radar className={`h-3 w-3 ${scanning ? "animate-spin" : ""}`} />{" "}
            {scanning ? t("app.pages.radar.scanning") : t("app.pages.radar.refresh")}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t("app.pages.radar.subtitle")}</p>
        {signals.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            {t("app.pages.radar.empty")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {signals.map((sig) => (
              <RadarCard
                key={sig.id}
                sig={sig}
                drafting={draftingId === sig.id}
                onDraft={() => writeDraft(sig.id)}
                onSent={() => useSignal(sig)}
                onDismiss={() => dropSignal(sig.id)}
              />
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="glass-strong rounded-2xl p-10 text-center text-sm text-muted-foreground">
          {t("app.pages.opportunities.loading")}
        </div>
      ) : items.length === 0 && meetings.length === 0 ? (
        <div className="space-y-4">
          <div className="glass-strong rounded-2xl p-10 text-center">
            <p className="text-muted-foreground text-sm mb-3">
              {t("app.pages.opportunities.noOppsTitle")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("app.pages.opportunities.noOppsBefore")}
              <Link to="/marketplace" className="text-[oklch(0.82_0.18_168)]">
                {t("app.pages.opportunities.marketplace")}
              </Link>
              {t("app.pages.opportunities.noOppsAfter")}
            </p>
          </div>
          {/* Say why it is empty. Matching cannot invent counterparts. */}
          <NetworkReach reach={reach} />
        </div>
      ) : (
        <>
          <div className="mb-4">
            <NetworkReach reach={reach} compact />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((o) => (
              <FitCard
                key={o.id}
                o={o}
                meeting={meetingFor === o.id}
                meetingDisabled={meetingFor !== null}
                meetingPhase={
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[oklch(0.82_0.18_168)] opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[oklch(0.82_0.18_168)]" />
                    </span>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={phase}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="text-muted-foreground"
                      >
                        {t(MEETING_PHASES[phase])}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                }
                onMeet={() => setBriefFor(o)}
                onStatus={(status) => decide(o.id, status)}
              />
            ))}
          </div>

          <div id="twin-meetings" className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <Bot className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />
              <h2 className="font-display text-lg font-semibold">
                {t("app.pages.opportunities.twinMeetings")}
              </h2>
              <span className="text-xs text-muted-foreground">
                {t("app.pages.opportunities.twinMeetingsSub")}
              </span>
            </div>
            {meetings.length === 0 ? (
              <div className="glass-strong rounded-2xl p-8 text-center text-sm text-muted-foreground">
                {t("app.pages.opportunities.noMeetings")}
              </div>
            ) : (
              <div className="space-y-3">
                {meetings.map((m) => {
                  const isOpen = openMeeting === m.id;
                  const v = m.verdict ?? {};
                  const score = Number(v.synergy_score ?? 0);
                  const scoreColor = score >= 70 ? "168" : score >= 40 ? "60" : "20";
                  const transcript: any[] = Array.isArray(m.transcript) ? m.transcript : [];
                  return (
                    <div key={m.id} className="glass-strong rounded-2xl overflow-hidden">
                      <button
                        onClick={() => setOpenMeeting(isOpen ? null : m.id)}
                        className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/5"
                      >
                        <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center text-black shrink-0">
                          <Handshake className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold truncate">
                            {m.company_a_name} × {m.company_b_name}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {v.summary ?? t("app.pages.opportunities.meetingFallback")}
                          </div>
                        </div>
                        {v.synergy_score != null && (
                          <div className="text-right shrink-0">
                            <div
                              className="font-display text-xl font-semibold"
                              style={{ color: `oklch(0.82 0.18 ${scoreColor})` }}
                            >
                              {score}%
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                              {t("app.pages.opportunities.synergy")}
                            </div>
                          </div>
                        )}
                        {isOpen ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="border-t border-border p-5 grid lg:grid-cols-2 gap-5">
                          <div>
                            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-3">
                              {t("app.pages.opportunities.transcript")}
                            </div>
                            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                              {transcript.map((turn: any, i: number) => (
                                <div
                                  key={i}
                                  className={`flex ${turn.speaker === "a" ? "justify-start" : "justify-end"}`}
                                >
                                  <div
                                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${turn.speaker === "a" ? "glass" : "bg-white/10"}`}
                                  >
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                                      {turn.name}
                                    </div>
                                    <div className="whitespace-pre-wrap">{turn.text}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                              {t("app.pages.opportunities.analystVerdict")}
                            </div>
                            {m.objective && (
                              <div className="rounded-xl glass border border-border p-3">
                                <div className="text-xs text-muted-foreground mb-1">
                                  {t("app.pages.opportunities.objectiveLabel", {
                                    defaultValue: "Objective for this meeting",
                                  })}
                                </div>
                                <div className="text-sm">{m.objective}</div>
                                {v.objective_note && (
                                  <div className="text-xs text-muted-foreground mt-1.5">
                                    {v.objective_met === true
                                      ? "✓ "
                                      : v.objective_met === false
                                        ? "✕ "
                                        : ""}
                                    {v.objective_note}
                                  </div>
                                )}
                              </div>
                            )}
                            {v.summary && <p className="text-sm">{v.summary}</p>}
                            <CitedList
                              items={v.opportunities}
                              label={t("app.pages.opportunities.oppsLabel")}
                              hue="oklch(0.82 0.18 168)"
                            />
                            <CitedList
                              items={v.risks}
                              label={t("app.pages.opportunities.openQuestions")}
                              hue="oklch(0.75 0.16 60)"
                            />
                            {v.next_step && (
                              <div className="rounded-xl glass border border-border p-3 text-sm">
                                <div className="text-xs text-muted-foreground mb-1">
                                  {t("app.pages.opportunities.recommendedNext")}
                                </div>
                                {v.next_step}
                              </div>
                            )}
                            {v.intro_message && (
                              <div className="rounded-xl glass border border-border p-3 text-sm">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="text-xs text-muted-foreground">
                                    {t("app.pages.opportunities.draftIntro")}
                                  </div>
                                  <button
                                    onClick={() => copyIntro(v.intro_message)}
                                    className="text-xs flex items-center gap-1 text-[oklch(0.82_0.18_168)] hover:underline"
                                  >
                                    <Copy className="h-3 w-3" /> {t("app.pages.opportunities.copy")}
                                  </button>
                                </div>
                                <p className="text-muted-foreground whitespace-pre-wrap">
                                  {v.intro_message}
                                </p>
                              </div>
                            )}
                            {m.owner_a === meetingsUid && (
                              <button
                                onClick={() => draftDeal(m.id)}
                                className="w-full rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium inline-flex items-center justify-center gap-1.5"
                              >
                                <Handshake className="h-3.5 w-3.5" />{" "}
                                {t("app.pages.deals.draftDeal")}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {briefFor && (
        <MeetingBrief
          opportunity={briefFor}
          onCancel={() => setBriefFor(null)}
          onStart={(objective) => meet(briefFor, objective)}
        />
      )}
    </DashboardShell>
  );
}

/**
 * What the owner wants out of the meeting, asked before it runs.
 *
 * The objective steers the initiating twin and the analyst then reports
 * explicitly on whether it was answered — so a meeting can be judged against
 * what it was for, not just on whether the two sides got along.
 */
function MeetingBrief({
  opportunity,
  onCancel,
  onStart,
}: {
  opportunity: any;
  onCancel: () => void;
  onStart: (objective: string | null) => void;
}) {
  const t = useHydratedTranslation();
  const [objective, setObjective] = useState("");

  const suggestions = [
    t("app.pages.opportunities.objSupplier", {
      defaultValue: "Find out whether they could supply us, and on what terms",
    }),
    t("app.pages.opportunities.objResell", {
      defaultValue: "Explore whether they would resell or refer our services",
    }),
    t("app.pages.opportunities.objCapacity", {
      defaultValue: "Check whether they have capacity for a project in the next quarter",
    }),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button
        aria-label={t("app.pages.contracts.close", { defaultValue: "Close" })}
        onClick={onCancel}
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
      />
      <div className="relative w-full sm:max-w-lg glass-strong rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="font-display text-lg font-semibold">
          {t("app.pages.opportunities.briefTitle", { defaultValue: "Brief your twin" })}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {t("app.pages.opportunities.briefSub", {
            defaultValue:
              "Say what you want to establish with {{name}}. Your twin drives the conversation toward it, and the analyst reports whether it got there.",
            name: opportunity.match_company ?? "them",
          })}
        </p>

        <textarea
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          rows={3}
          maxLength={400}
          autoFocus
          placeholder={t("app.pages.opportunities.briefPlaceholder", {
            defaultValue: "e.g. Find out whether they can machine titanium parts under 50mm",
          })}
          className="mt-3 w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
        />

        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => setObjective(s)}
              className="rounded-full glass border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-white/5 text-left"
            >
              {s}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => onStart(objective.trim() || null)}
            className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium inline-flex items-center gap-1.5"
          >
            <Handshake className="h-3.5 w-3.5" />
            {t("app.pages.opportunities.startMeeting")}
          </button>
          <button
            onClick={onCancel}
            className="rounded-full glass border border-border px-4 py-2 text-sm hover:bg-white/5"
          >
            {t("app.pages.contracts.close", { defaultValue: "Close" })}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t("app.pages.opportunities.briefOptional", {
            defaultValue: "Optional — but without one the meeting has nothing to aim at.",
          })}
        </p>
      </div>
    </div>
  );
}
